import { isRecord } from '@/utils/objects';
import type { RelationshipNode, ConditionFilter, ParseOptions } from '../types';
import { getOperatorLabel, formatValue, toNumber, toString } from './parse-helpers';

function parseRelationshipFilters(items: Record<string, unknown>[]): ConditionFilter[] {
  const results: ConditionFilter[] = [];
  for (const item of items) {
    const isObjectAttribute = item.related_object_attribute != null;
    const isRelationshipAttribute = item.relationship_attribute != null;
    const data = item.related_object_attribute ?? item.relationship_attribute ?? item;
    if (!isRecord(data)) continue;
    const field = toString(data.field ?? data.attribute);
    if (field == null) continue;
    const op = toString(data.operator);
    const inverse = data.inverse === true;
    results.push({
      field,
      operator: op != null ? getOperatorLabel(op, inverse) : 'is',
      value: data.value != null ? formatValue(data.value, op) : undefined,
      value2: data.value2 != null ? formatValue(data.value2, op) : undefined,
      sourceType: filterSource(item.field_source, isObjectAttribute, isRelationshipAttribute),
    });
  }
  return results;
}

function extractRelationshipFilters(raw: unknown): { filters: ConditionFilter[]; operator: 'and' | 'or' } | null {
  if (raw == null) return null;
  if (Array.isArray(raw)) return { filters: parseRelationshipFilters(raw), operator: 'and' };
  if (isRecord(raw)) {
    if (Array.isArray(raw.and)) {
      return { filters: parseRelationshipFilters(raw.and.filter(isRecord)), operator: 'and' };
    }
    if (Array.isArray(raw.or)) return { filters: parseRelationshipFilters(raw.or.filter(isRecord)), operator: 'or' };
  }
  return null;
}

function buildNode(
  typeId: number | undefined,
  objectName: string | undefined,
  inverse: boolean,
  filtersData: unknown,
  andFlag?: boolean,
): RelationshipNode {
  const parsed = extractRelationshipFilters(filtersData);
  return {
    type: 'relationship',
    objectTypeId: typeId,
    objectTypeName: objectName,
    inverse,
    filters: parsed?.filters,
    filtersOperator: parsed?.operator ?? (andFlag === true ? 'and' : undefined),
  };
}

/**
 * Extract filters from the non-relationship items in a relationship group.
 * Items can be flat filter objects or wrapped in { and: [...] } / { or: [...] } groups.
 */
function extractFiltersFromRelationshipGroup(
  items: Record<string, unknown>[],
): { filters: ConditionFilter[]; operator: 'and' | 'or' } | null {
  if (items.length === 0) return null;

  // Check if there's a single group wrapper ({ and: [...] } or { or: [...] })
  if (items.length === 1) {
    const item = items[0];
    if ('and' in item && Array.isArray(item.and)) {
      return { filters: parseRelationshipFilters(item.and), operator: 'and' };
    }
    if ('or' in item && Array.isArray(item.or)) {
      return { filters: parseRelationshipFilters(item.or), operator: 'or' };
    }
  }

  // Flat filter items
  const filters = parseRelationshipFilters(items);
  return filters.length > 0 ? { filters, operator: 'and' } : null;
}

/** A batch update recipient's `related` row, marked with the objects and people it relates. */
function withRelatedAs(node: RelationshipNode, raw: unknown, options: ParseOptions): RelationshipNode {
  if (!isRecord(raw) || raw.type !== 'related' || options.readsRelatedAsRecipients !== true) {
    return node;
  }
  return {
    ...node,
    relatedAs: { objectSource: toString(raw.object_source), personSource: toString(raw.person_source) },
  };
}

/**
 * Parse relationship conditions.
 * Handles: { relationship: { type_id, inverse } }
 *          { type: 3, relationship_filter: { type_id, inverse, refinement } }
 *          { type: 'relationship', type_id, inverse }
 *          { and: [{ relationship: {...} }, ...filters] }
 */
export function parseRelationship(raw: Record<string, unknown>, options: ParseOptions = {}): RelationshipNode | null {
  // Ember format: { relationship: { type_id, inverse } }
  if ('relationship' in raw && isRecord(raw.relationship)) {
    const relationship = raw.relationship;
    return buildNode(
      toNumber(relationship.type_id),
      toString(raw.object_type_name ?? relationship.object_name),
      relationship.inverse === true,
      relationship.refinement ?? relationship.filters,
      relationship.and === true,
    );
  }

  // Backend type: 3 with relationship_filter
  if ((raw.type === 3 || raw.type === '3') && isRecord(raw.relationship_filter)) {
    const rf = raw.relationship_filter;
    return buildNode(
      toNumber(rf.type_id),
      toString(raw.object_type_name),
      rf.inverse === true,
      rf.refinement ?? rf.filters,
      rf.and === true,
    );
  }

  // { relationship_filter: {...} } without type: 3
  if ('relationship_filter' in raw && isRecord(raw.relationship_filter)) {
    const rf = raw.relationship_filter;
    return buildNode(
      toNumber(rf.type_id),
      toString(raw.object_type_name),
      rf.inverse === true,
      rf.refinement ?? rf.filters,
      rf.and === true,
    );
  }

  // Direct: { type: 'relationship' | 'related' }
  if (raw.type === 'relationship' || raw.type === 'related') {
    return withRelatedAs(
      buildNode(
        toNumber(raw.object_type_id ?? raw.type_id),
        toString(raw.object_type_name ?? raw.objectName ?? raw.object_name),
        raw.inverse === true,
        raw.refinement ?? raw.filters,
      ),
      raw,
      options,
    );
  }

  // { and: [{ relationship: {...} }, ...filters or { or/and: [...filters] }] }
  if ('and' in raw && Array.isArray(raw.and)) {
    const array = raw.and.filter(
      (item): item is Record<string, unknown> => item != null && typeof item === 'object' && !Array.isArray(item),
    );
    const relationshipItem = array.find(
      (item) => 'relationship' in item && typeof item.relationship === 'object' && item.relationship != null,
    );
    if (relationshipItem != null && isRecord(relationshipItem.relationship)) {
      const relationship = relationshipItem.relationship;
      const nonRelationshipItems = array.filter((item) => !('relationship' in item));

      // Filters may be flat items or wrapped in { and: [...] } / { or: [...] } groups
      const parsed = extractFiltersFromRelationshipGroup(nonRelationshipItems);
      return {
        type: 'relationship',
        objectTypeId: toNumber(relationship.type_id),
        objectTypeName: toString(raw.object_type_name ?? relationship.object_name),
        inverse: relationship.inverse === true,
        filters: parsed?.filters,
        filtersOperator: parsed?.operator,
      };
    }
  }

  return null;
}

/**
 * Which attributes a filter reads. A segment's relationship filter wraps its
 * row in the attribute kind; a workflow's `related` refinement row names it
 * in `field_source`.
 */
function filterSource(
  fieldSourceValue: unknown,
  isObjectAttribute: boolean,
  isRelationshipAttribute: boolean,
): ConditionFilter['sourceType'] {
  const fieldSource = toString(fieldSourceValue) ?? '';
  if (isObjectAttribute || fieldSource.endsWith('object_attribute')) {
    return 'object';
  }
  if (isRelationshipAttribute || fieldSource.endsWith('relationship_attribute')) {
    return 'relationship';
  }
  return undefined;
}
